import { createContext, PropsWithChildren, useContext, useEffect } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { ProductActivationStatusResponse } from "@atlassiansox/asc-stargate";

import { useSearchParameters } from "@/components/signup/hooks";
import {
    collectionOfferingIds,
    orgBasedOfferingIdMap,
    ProductKey
} from "@/components/signup/utils";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src";
import { COMMA_REGEX } from "@/templates/buy/cloud/post-order-provisioning/src";
import { getDataFoundationEvent } from "@/utils/data-foundations/data-foundation-types";
import { useLocation } from "@/utils/location-provider";

import { getTopLevelDomain } from "../utils/create-product-url";

import { useCloudStatus } from ".";

export function useCloudStatusDirectBuyRedirection() {
    return useContext(CloudStatusDirectBuyRedirectionContext);
}

const CloudStatusDirectBuyRedirectionContext = createContext({});

export const CloudStatusDirectBuyRedirectionProvider =
    /* istanbul ignore next */
    ({ children }: PropsWithChildren) => {
        const {
            createCloudStatus,
            readyPollCloudStatus,
            pollCloudStatus,
            setCreateCloudStatus,
            productActivationStatusResponses,
            isProduction
        } = useCloudStatus();
        const { createAnalyticsEvent } = useAnalyticsEvents();
        const { products, edition, siteName } = useSearchParameters();
        const stargateClient = useBxpWebStargateClient();
        const { activateFor, orderId } = useSearchParameters();
        const location = useLocation();
        const productKey = (
            products.length > 0 ? products[0] : undefined
        ) as ProductKey;

        const getCloudId = (
            productActivationStatusResponse: ProductActivationStatusResponse
        ): string => {
            const resourceAris =
                productActivationStatusResponse.resourcePackages[0]
                    ?.resourceAris;
            const cloudId = resourceAris
                ?.filter((ari) =>
                    ari.startsWith("ari:cloud:platform::site/")
                )[0]
                ?.split("/")[1];

            if (!cloudId) {
                throw new Error("no cloudId found");
            }

            return cloudId;
        };

        const searchParams = new URLSearchParams(location.search);
        const offeringIdParam = searchParams.get("offeringId") || "";
        const offeringIds = offeringIdParam.split(COMMA_REGEX);
        const orgIdParam = searchParams.get("orgId") || "";
        let collectionOfferingId = "",
            collectionProductKey: string | undefined;
        const isCollection = offeringIds.some((offeringId) => {
            if (collectionOfferingIds.has(offeringId)) {
                collectionOfferingId = offeringId;
                collectionProductKey = orgBasedOfferingIdMap[offeringId];
                return true;
            }
            return false;
        });
        const isServCo =
            isCollection &&
            !!collectionOfferingId &&
            !!collectionProductKey &&
            collectionProductKey.startsWith("SERVICE_COLLECTION");

        const homeUrlOrgPath = orgIdParam ? `/o/${orgIdParam}` : "";
        const homeUrl = isProduction
            ? `https://home.atlassian.com${homeUrlOrgPath}`
            : `https://home.stg.atlassian.com${homeUrlOrgPath}`;

        const getMissingProperties = (
            a: Record<string, string>,
            b: Record<string, string>
        ): Record<string, string> => {
            const missingProperties: Record<string, string> = {};
            for (const key in b) {
                if (b.hasOwnProperty(key) && b[key] && !a.hasOwnProperty(key)) {
                    missingProperties[key] = b[key];
                }
            }
            return missingProperties;
        };
        const getRedirectUrlForExistingSite = (params: {
            products: Record<string, string>;
            cloudId: string;
            orderId: string;
        }): string | undefined => {
            const data = localStorage.getItem(
                `existingProducts--orderId:${params.orderId}`
            );
            if (!data) {
                return undefined;
            }
            const prevProducts = JSON.parse(data);
            const newProducts = getMissingProperties(
                prevProducts,
                params.products
            );
            if (Object.values(newProducts).length === 1) {
                return Object.values(newProducts)[0];
            } else {
                // redirecting if new products are more than 1
                return homeUrl;
            }
        };
        const onSuccessPostOrder = async (
            data: ProductActivationStatusResponse
        ) => {
            try {
                if (isCollection) {
                    if (isServCo) {
                        // Redirect ServCo to JSM product page
                        const instanceUrl = `https://${encodeURIComponent(
                            String(siteName)
                        )}${getTopLevelDomain(
                            isProduction
                        )}/secure/WelcomeToSD.jspa?decorator=jsd-onboarding`;
                        window.location.assign(instanceUrl);
                        return true;
                    }
                    if (isProduction) {
                        window.location.assign(homeUrl);
                    } else {
                        window.location.assign(homeUrl);
                    }
                    return true;
                }

                const cloudId = getCloudId(data);
                const cloudProducts = await stargateClient.getCloudProductInfo({
                    cloudId,
                    webPlatform: "web-platform"
                });

                let redirectUrl: string | undefined;
                if (activateFor === "existing") {
                    if (!orderId) {
                        // tracking.trackEvent('orderId query param missing', {
                        //   eventComponent: 'orderId query param',
                        //   event: 'missing',
                        // });
                        setCreateCloudStatus("error");
                        return false;
                    }
                    redirectUrl = getRedirectUrlForExistingSite({
                        products: cloudProducts,
                        orderId,
                        cloudId
                    });
                } else {
                    if (Object.values(cloudProducts).length === 1) {
                        redirectUrl = Object.values(cloudProducts)[0];
                    } else {
                        // redirecting if cloud products are more than 1
                        redirectUrl = homeUrl;
                    }
                }

                if (!redirectUrl) {
                    setCreateCloudStatus("error");
                    return false;
                } else {
                    const isLoom =
                        cloudProducts && Boolean(cloudProducts["loom"]);
                    if (isLoom) {
                        const searchParams = new URLSearchParams(
                            window.location.search
                        );

                        const loomSource = searchParams.get("source");

                        const loomRedirectSearchParams: Record<string, string> =
                            {
                                ...(loomSource && { source: loomSource })
                            };

                        if (isProduction) {
                            loomRedirectSearchParams[
                                "redirect_after"
                            ] = `https://www.loom.com/workspace/cloud/${cloudId}`;

                            redirectUrl = `https://loom.com/api/auth/atlassian?${new URLSearchParams(
                                loomRedirectSearchParams
                            ).toString()}`;
                        } else {
                            loomRedirectSearchParams[
                                "redirect_after"
                            ] = `https://stage.loom.com/workspace/cloud/${cloudId}`;

                            redirectUrl = `https://stage.loom.com/api/auth/atlassian?${new URLSearchParams(
                                loomRedirectSearchParams
                            ).toString()}`;
                        }
                    }
                    window.location.assign(redirectUrl);
                }
                return true;
            } catch (error) {
                // tracking.trackEvent("provisioning success redirect failed", {
                //     eventComponent: "provisioning success redirect",
                //     event: "failed"
                // });
                // const responseTraceId = getTraceId(
                //     error as UnsucessfulFetchError
                // );
                // if (responseTraceId) {
                //     setTraceId(responseTraceId);
                // }
                setCreateCloudStatus("error");
                return false;
            }
        };

        useEffect(() => {
            const productActivationStatusResponse =
                productActivationStatusResponses[0];
            if (
                createCloudStatus !== "success" ||
                !productActivationStatusResponse
            ) {
                return;
            }

            onSuccessPostOrder(productActivationStatusResponse).then(
                (success) => {
                    if (success) {
                        createAnalyticsEvent({
                            ...getDataFoundationEvent(
                                "ProductNavigationInitialized"
                            ),
                            actionSubjectId: "directBuy",
                            attributes: {
                                productKey,
                                edition
                            }
                        }).fire();
                    }
                }
            );
        }, [createCloudStatus, productActivationStatusResponses]);

        useEffect(() => {
            if (readyPollCloudStatus) {
                pollCloudStatus();
            }
        }, [readyPollCloudStatus, pollCloudStatus]);

        return (
            <CloudStatusDirectBuyRedirectionContext.Provider value={{}}>
                {children}
            </CloudStatusDirectBuyRedirectionContext.Provider>
        );
    };
